import { all, call, put, takeEvery } from 'redux-saga/effects';
import { IRequestParams, request } from 'helpers/request';
import { AxiosResponse } from 'axios';
import { ICategory, ISurveyService } from 'models';
import { logSagaErrorMessage } from 'utils';
import { requestSagaFactory } from 'helpers';
import { CONSTANTS_TEMPLATES } from '../utils';
import * as types from '../ActionTypes';
import {
	getCategoryList,
	postCategory,
	postService,
	updateService,
	getPartnerCategoryList,
	getTemplatePartnerCategories,
} from '../services.actions';
import { ICategoryFormFields, IServiceFormFields } from '../components';

const getCategoryListSaga = requestSagaFactory({
	url: `${CONSTANTS_TEMPLATES.baseUrl}/categories`,
	actionApi: getCategoryList,
	logErrorMessage: 'getCategoryList',
});

function* getPartnerCategoryListSaga({
	payload,
}: {
	payload: {
		partnerId?: string;
		templateId?: number;
	};
	type: string;
}) {
	try {
		yield put(getPartnerCategoryList.loading());

		const getCategoriesParams: IRequestParams = {
			method: 'GET',
			params: payload,
			url: `${CONSTANTS_TEMPLATES.baseUrl}/categories`,
		};

		const { data }: AxiosResponse<ICategory> = yield call(
			request,
			getCategoriesParams,
		);

		payload.templateId
			? yield put(getTemplatePartnerCategories.success(data))
			: yield put(getPartnerCategoryList.success(data));
	} catch (e) {
		logSagaErrorMessage('getPartnerCategories', e);
		yield put(getPartnerCategoryList.fail(e));
	}
}

function* postCategorySaga({
	payload,
}: {
	payload: ICategoryFormFields;
	type: string;
}) {
	try {
		yield put(postCategory.loading());

		const postCategoriesParams: IRequestParams = {
			method: 'POST',
			data: payload,
			url: `${CONSTANTS_TEMPLATES.baseUrl}/categories`,
		};

		const { data }: AxiosResponse<ICategory> = yield call(
			request,
			postCategoriesParams,
		);

		yield put(postCategory.success(data));
	} catch (e) {
		logSagaErrorMessage('getCategories', e);
		yield put(postCategory.fail(e));
	}
}

function* postServiceSaga({
	payload,
}: {
	payload: IServiceFormFields;
	type: string;
}) {
	try {
		yield put(postService.loading());

		const postServiceParams: IRequestParams = {
			method: 'POST',
			data: payload,
			url: `${CONSTANTS_TEMPLATES.baseUrl}/services`,
		};

		const { data }: AxiosResponse<ISurveyService> = yield call(
			request,
			postServiceParams,
		);

		yield put(postService.success({ data, id: payload.categoryId }));
		yield put(
			getPartnerCategoryList.success([
				{
					id: payload.categoryId,
					surveyServices: [data],
				},
			]),
		);
	} catch (e) {
		logSagaErrorMessage('postService', e);
		yield put(postService.fail(e));
	}
}

export function* updateServiceSaga({
	payload,
}: {
	payload: Partial<ISurveyService> & {
		templateId?: number;
		categoryId?: number;
	};
	type: string;
}) {
	try {
		yield put(updateService.loading());

		const { templateId, categoryId, id, name, serviceExamples } = payload;

		const updateServiceParams: IRequestParams = {
			method: 'PATCH',
			url: `${CONSTANTS_TEMPLATES.baseUrl}/services/${id}`,
			params: { templateId, categoryId },
			data: JSON.stringify({ name, serviceExamples }),
		};

		const { data }: AxiosResponse<ICategory[]> = yield call(
			request,
			updateServiceParams,
		);

		yield put(updateService.success(data));
	} catch (e) {
		logSagaErrorMessage('updateService', e);
		yield put(updateService.fail(e));
	}
}

export function* servicesSaga() {
	yield all([
		takeEvery(types.GET_CATEGORY_LIST.DEFAULT, getCategoryListSaga),
		takeEvery(
			types.GET_PARTNER_CATEGORY_LIST.DEFAULT,
			getPartnerCategoryListSaga,
		),
		takeEvery(types.POST_CATEGORY.DEFAULT, postCategorySaga),
		takeEvery(types.POST_SERVICE.DEFAULT, postServiceSaga),
		takeEvery(types.PATCH_SERVICE.DEFAULT, updateServiceSaga),
	]);
}
